import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Download, Lock, Rocket, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { ErrorState, HeaderRow, LoadingState, TableShell, TH } from "@/components/PageUi";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiErrorMessage, apiErrorType } from "@/lib/api-error";
import { formatCalendarDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import {
  AccountSelect,
  bankAccountLabel,
  money,
  moneyOrBlank,
  num,
  useBankAccounts,
  useChart,
  useCurrentBook,
} from "./shared";
import type { Book, ControlAccounts, LedgerAccount, OpeningBalances, UpdateBookRequest } from "./types";

export function BooksSetup() {
  const { canManage } = useBooks();
  const current = useCurrentBook();
  const book = current.data;

  return (
    <PageContainer>
      <Link to="/books" className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-3.5" /> Books
      </Link>
      <PageHeader
        eyebrow="Books"
        title="Set up books"
        subtitle="Five steps. Nothing posts until you activate, and until then every step can be changed."
      />
      {!canManage ? (
        <ErrorState message="Only an owner or admin can set up the books." />
      ) : current.loading && book === undefined ? (
        <LoadingState label="Loading your books" />
      ) : current.error ? (
        <ErrorState message={apiErrorMessage(current.error, "Couldn't load your books.")} onRetry={current.reload} />
      ) : book?.status === "Active" ? (
        <ErrorState message="These books are already active. Settings has what can still change." />
      ) : (
        <div className="space-y-4">
          {/* A closed book is history: setup starts new books beside it (D14). */}
          <StartDateStep book={book?.status === "Setup" ? book : null} onStarted={current.reload} />
          {book?.status === "Setup" ? <SetupSteps book={book} reload={current.reload} /> : null}
        </div>
      )}
    </PageContainer>
  );
}

function Step({
  n,
  title,
  done,
  hint,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span
            className={cn(
              "grid size-6 place-items-center rounded-full border text-xs tabular-nums",
              done && "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
            )}
          >
            {done ? <Check className="size-3.5" /> : n}
          </span>
          {title}
          {hint ? <InfoHint label={`About ${title.toLowerCase()}`}>{hint}</InfoHint> : null}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/**
 * First days of months from `latest` back to `earliest`, newest first: the only dates the server accepts. With no
 * bank history yet there is no lower bound, so two years back is offered.
 */
function startDateOptions(earliest: string | null | undefined, latest: string) {
  const [y, m] = latest.split("-").map(Number);
  const options: string[] = [];
  for (let i = 0; i < 120; i++) {
    const d = new Date(Date.UTC(y!, m! - 1 - i, 1)).toISOString().slice(0, 10);
    if (earliest ? d < earliest : i >= 24) break;
    options.push(d);
  }
  return options;
}

function StartDateStep({ book, onStarted }: { book: Book | null; onStarted: () => Promise<void> }) {
  const { books } = useBooks();
  const { links } = useBooks();
  const [startDate, setStartDate] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const range = useLoad(() => books.GET("/v1/books/start-dates", {}), [books], { enabled: !book });
  const options = range.data?.latest ? startDateOptions(range.data.earliest, range.data.latest) : [];
  // The API's refusal, before or after a try: the same check backs GET /books/start-dates and POST /books.
  const blocked = refusal ?? range.data?.blockedReason?.detail ?? null;

  async function start() {
    setSaving(true);
    const { error } = await books.POST("/v1/books", { body: { startDate } });
    setSaving(false);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't start the books."));
      if (apiErrorType(error).includes("Books.AccountingNotConnected")) setRefusal(apiErrorMessage(error, ""));
      return;
    }
    setRefusal(null);
    toast.success("Books started. Next, confirm the control accounts.");
    await onStarted();
  }

  if (book) {
    return (
      <Step n={1} title="Start date" done>
        <p className="text-sm">
          The books start on <span className="font-semibold">{formatCalendarDate(book.startDate ?? "")}</span>. Opening
          balances are your platform's trial balance at the end of {formatCalendarDate(book.openingBalanceDate ?? "")}.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">To pick another date, close these books in Settings and start again.</p>
      </Step>
    );
  }

  return (
    <Step
      n={1}
      title="Start date"
      done={false}
      hint="The first day of a month. Your bank accounts' fetched history must reach back to it, so the cash side of the books has no gap."
    >
      <div className="flex flex-col gap-3 @lg:flex-row @lg:items-end">
        <div className="space-y-1.5 @lg:w-72">
          <Label htmlFor="books-start">Start the books on</Label>
          <Select value={startDate} onValueChange={setStartDate} disabled={options.length === 0}>
            <SelectTrigger id="books-start" className="w-full">
              <SelectValue placeholder={range.loading ? "Loading dates" : "Choose a month"} />
            </SelectTrigger>
            <SelectContent>
              {options.map((d) => (
                <SelectItem key={d} value={d}>
                  {formatCalendarDate(d)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={start} disabled={!startDate || saving || Boolean(blocked)}>
          {saving ? <Spinner className="size-4" /> : null} Start books
        </Button>
      </div>
      {blocked ? (
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm @lg:flex-row @lg:items-center @lg:justify-between">
          <p className="text-amber-900 dark:text-amber-200">{blocked}</p>
          <Button asChild size="sm" variant="outline" className="shrink-0">
            {range.data?.blockedReason?.code === "Books.InvalidStartDate" && !refusal ? (
              <Link to={links.bankConnections}>Bank connections</Link>
            ) : (
              <Link to={links.accountingConnection}>Organisation, Accounting</Link>
            )}
          </Button>
        </div>
      ) : null}
      {range.error ? (
        <p className="mt-2 text-xs text-destructive">
          {apiErrorMessage(range.error, "Couldn't load the dates the books can start on.")}{" "}
          <button type="button" className="underline" onClick={() => void range.reload()}>
            Try again
          </button>
        </p>
      ) : null}
    </Step>
  );
}

const CONTROL_FIELDS: Array<{ key: keyof ControlAccounts; label: string; hint: string; optional?: boolean }> = [
  { key: "accountsReceivableLedgerAccountId", label: "Accounts receivable", hint: "What customers owe. Invoices post here." },
  { key: "accountsPayableLedgerAccountId", label: "Accounts payable", hint: "What you owe suppliers. Bills post here." },
  { key: "vatLedgerAccountId", label: "VAT", hint: "VAT on sales and reclaimable VAT on purchases." },
  { key: "uncategorisedLedgerAccountId", label: "Uncategorised", hint: "Bank transactions park here until matched or categorised." },
  {
    key: "retainedEarningsLedgerAccountId",
    label: "Retained earnings",
    hint: "Takes earlier years' profit, as on your platform's balance sheet.",
  },
  {
    key: "openingBalanceDifferenceLedgerAccountId",
    label: "Opening-balance difference",
    hint: "Takes trial-balance rows the platform reports without an account.",
  },
  {
    key: "stripeClearingLedgerAccountId",
    label: "Stripe clearing",
    hint: "Card payments taken through Stripe, until paid out. In Xero, a current asset with payments enabled.",
    optional: true,
  },
  { key: "stripeFeesLedgerAccountId", label: "Stripe fees", hint: "Stripe's processing fees on each payout.", optional: true },
];

function SetupSteps({ book, reload }: { book: Book; reload: () => Promise<void> }) {
  const { books } = useBooks();
  const chart = useChart();
  const banks = useBankAccounts();
  const checklist = book.checklist ?? {};
  const [saving, setSaving] = useState<string | null>(null);

  async function patch(body: UpdateBookRequest, what: string) {
    setSaving(what);
    const { error } = await books.PATCH("/v1/books/current", { body });
    setSaving(null);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't save the change."));
      return;
    }
    await reload();
  }

  if ((chart.loading && !chart.data) || (banks.loading && !banks.data)) return <LoadingState label="Loading your chart of accounts" />;
  if (chart.error) {
    return <ErrorState message={apiErrorMessage(chart.error, "Couldn't load the chart of accounts.")} onRetry={chart.reload} />;
  }
  const accounts = (chart.data ?? []).filter((a) => a.isActive !== false);
  const stripeMissing = checklist.missing?.find((m) => /stripe/i.test(m));

  return (
    <>
      <Step
        n={2}
        title="Control accounts"
        done={Boolean(checklist.controlAccounts)}
        hint="Accounts your platform posts to without being told. Pre-filled from your chart; they lock once the books are active."
      >
        <div className="grid gap-4 @lg:grid-cols-2">
          {CONTROL_FIELDS.map((f) => (
            <AccountSelect
              key={f.key}
              id={`control-${f.key}`}
              label={f.optional ? `${f.label} (if you take card payments)` : f.label}
              hint={f.hint}
              optional={f.optional}
              value={book.controlAccounts?.[f.key]}
              accounts={accounts}
              disabled={saving !== null}
              onChange={(id) => patch({ [f.key]: id }, f.key)}
            />
          ))}
        </div>
        {/* Required once the organisation takes Stripe payments (D29); the server says so in the checklist. */}
        {stripeMissing ? (
          <p className="mt-4 rounded-lg bg-amber-500/15 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
            {stripeMissing.charAt(0).toUpperCase() + stripeMissing.slice(1)}.
          </p>
        ) : null}
      </Step>

      <Step
        n={3}
        title="Bank mapping"
        done={Boolean(checklist.bankMapping)}
        hint="Each bank account in your chart is checked against a connected bank account, or marked not tracked: payments still post to it, but its balance is not checked."
      >
        {(book.bankAccounts ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Your chart has no bank accounts.</p>
        ) : (
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Account in your chart</TableHead>
                  <TableHead className={TH}>Checked against</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {(book.bankAccounts ?? []).map((a) => (
                  <TableRow key={a.ledgerAccountId}>
                    <TableCell>
                      <div className="font-medium">{a.code ? `${a.code} · ${a.name}` : a.name}</div>
                      {a.inOpeningBalances && !a.mapped ? (
                        <div className="text-xs text-amber-700 dark:text-amber-400">Has an opening balance: map it to activate</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="w-80">
                      <Select
                        value={!a.mapped ? undefined : a.tracked && a.bankAccountId ? a.bankAccountId : NOT_TRACKED}
                        onValueChange={(v) =>
                          patch(
                            {
                              bankAccounts: [
                                v === NOT_TRACKED
                                  ? { ledgerAccountId: a.ledgerAccountId!, bankAccountId: null, tracked: false }
                                  : { ledgerAccountId: a.ledgerAccountId!, bankAccountId: v, tracked: true },
                              ],
                            },
                            `bank-${a.ledgerAccountId}`,
                          )
                        }
                        disabled={saving !== null}
                      >
                        <SelectTrigger className={cn("w-full", !a.mapped && "border-amber-500/60")}>
                          <SelectValue placeholder="Choose" />
                        </SelectTrigger>
                        <SelectContent>
                          {banks.accounts.map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {bankAccountLabel(b)}
                            </SelectItem>
                          ))}
                          <SelectItem value={NOT_TRACKED}>Not tracked</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
        )}
      </Step>

      <OpeningBalancesStep book={book} accounts={accounts} reload={reload} />
      <ActivateStep book={book} reload={reload} />
    </>
  );
}

const NOT_TRACKED = "__not_tracked__";

function OpeningBalancesStep({
  book,
  accounts,
  reload,
}: {
  book: Book;
  accounts: LedgerAccount[];
  reload: () => Promise<void>;
}) {
  const { books } = useBooks();
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<OpeningBalances | null>(null);
  const imported = Boolean(book.checklist?.openingBalances);

  async function importBalances() {
    setImporting(true);
    const { data, error } = await books.POST("/v1/books/current/opening-balances", {});
    setImporting(false);
    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't import the opening balances."));
      return;
    }
    setResult(data);
    toast.success(data.reimported === false ? "Opening balances unchanged since the last import." : "Opening balances imported.");
    await reload();
  }

  const names = new Map(accounts.map((a) => [a.id, a]));
  const balanced = result ? Math.abs(num(result.totalDebit) - num(result.totalCredit)) < 0.005 : false;
  const unmapped = result?.rows?.filter((r) => r.unmapped) ?? [];

  return (
    <Step
      n={4}
      title="Opening balances"
      done={imported}
      hint="Your platform's trial balance at the opening-balance date, posted as the books' first entry. Re-import if the platform changes before you activate."
    >
      <div className="flex flex-wrap items-center gap-3">
        <Button variant={imported ? "outline" : "default"} onClick={importBalances} disabled={importing}>
          {importing ? <Spinner className="size-4" /> : <Download className="size-4" />}
          {imported ? "Re-import" : "Import"} trial balance at {formatCalendarDate(book.openingBalanceDate ?? "")}
        </Button>
        {imported && !result ? (
          <Link to={`/books/trial-balance?date=${book.openingBalanceDate}`} className="text-sm underline-offset-4 hover:underline">
            Check it against the platform
          </Link>
        ) : null}
      </div>

      {result ? (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2 text-sm">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium",
                balanced
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                  : "bg-destructive/10 text-destructive",
              )}
            >
              {balanced ? <Check className="size-4" /> : <TriangleAlert className="size-4" />}
              {balanced ? "Balances" : "Does not balance"}: debits {money(result.totalDebit)}, credits {money(result.totalCredit)}
            </span>
            {unmapped.length > 0 ? (
              <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-500/15 px-2 py-1 font-medium text-amber-800 dark:text-amber-300">
                <TriangleAlert className="size-4" />
                {unmapped.length} row{unmapped.length === 1 ? "" : "s"} without an account, {money(result.unmappedNet)} to the
                opening-balance difference
              </span>
            ) : null}
          </div>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Platform account</TableHead>
                  <TableHead className={TH}>Posted to</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Debit</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Credit</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {(result.rows ?? []).map((r, i) => {
                  const to = r.ledgerAccountId ? names.get(r.ledgerAccountId) : undefined;
                  return (
                    <TableRow key={`${r.platformAccountId ?? r.name}-${i}`} className={cn(r.unmapped && "bg-amber-500/10")}>
                      <TableCell>{r.code ? `${r.code} · ${r.name}` : r.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {r.unmapped ? (
                          <span className="font-medium text-amber-800 dark:text-amber-300">Unmapped: opening-balance difference</span>
                        ) : (
                          to?.name ?? "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{moneyOrBlank(r.debit)}</TableCell>
                      <TableCell className="text-right tabular-nums">{moneyOrBlank(r.credit)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2} className="font-semibold">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{money(result.totalDebit)}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{money(result.totalCredit)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableShell>
        </div>
      ) : null}
    </Step>
  );
}

function ActivateStep({ book, reload }: { book: Book; reload: () => Promise<void> }) {
  const { books } = useBooks();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"idle" | "activating" | "backfilling">("idle");
  const [progress, setProgress] = useState<Book["progress"]>(null);
  const checklist = book.checklist ?? {};
  const ready = Boolean(checklist.startDate && checklist.controlAccounts && checklist.bankMapping && checklist.openingBalances);

  async function activate() {
    setPhase("activating");
    const { error } = await books.POST("/v1/books/current/activate", {});
    if (error) {
      setPhase("idle");
      toast.error(apiErrorMessage(error, "Couldn't activate the books."));
      return;
    }
    // Backfill runs through the posting consumers: done once it has counted everything (backfilledAt) and nothing
    // waits or is in flight. A slow backfill carries on in the background after two minutes.
    setPhase("backfilling");
    const deadline = Date.now() + 120_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2000));
      const { data } = await books.GET("/v1/books/current", {});
      const p = data?.progress;
      setProgress(p ?? null);
      if (p?.backfilledAt && num(p.waiting) + num(p.inFlight) === 0) break;
    }
    toast.success("Books active.");
    await reload();
    navigate("/books");
  }

  return (
    <Step
      n={5}
      title="Activate"
      done={false}
      hint="Posts the opening balances, then every invoice, bill, expense and bank transaction dated on or after the start date. Control accounts lock."
    >
      {phase === "backfilling" ? (
        <BackfillProgress startDate={book.startDate} progress={progress} />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={activate} disabled={!ready || phase !== "idle"}>
            {phase === "activating" ? <Spinner className="size-4" /> : <Rocket className="size-4" />} Activate books
          </Button>
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="size-3.5" /> Control accounts can't change after this.
          </span>
        </div>
      )}
      {!ready && checklist.missing && checklist.missing.length > 0 ? (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {checklist.missing.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      ) : null}
    </Step>
  );
}

function BackfillProgress({ startDate, progress }: { startDate?: string; progress: Book["progress"] }) {
  const total = num(progress?.total);
  const done = num(progress?.posted) + num(progress?.skipped) + num(progress?.failed);
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-sm">
        <Spinner className="size-4" />
        {!progress?.backfilledAt
          ? `Preparing: finding your documents since ${formatCalendarDate(startDate ?? "")}`
          : total === 0
            ? "Nothing to post since the start date."
            : `Posting your documents: ${done} of ${total}`}
      </p>
      <div className="h-1.5 w-full max-w-md overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-[width]" style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} />
      </div>
    </div>
  );
}
